import { useServices } from '@/app/providers';
import { isSectionAdmin, useAbility } from '@/modules/identity/application/ability';
import { useI18n } from '@/shared/i18n';
import { AlertTriangle, BookOpen, Calendar, ChevronRight, FileText, Landmark, Scale, TrendingUp } from 'lucide-react';
import { useMemo } from 'react';

export type ReportType =
  | 'account-statement'
  | 'delinquent'
  | 'annual-billing'
  | 'income-expense'
  | 'monthly-statement'
  | 'journal'
  | 'bank-reconciliation';

interface ReportsIndexPageProps {
  onNavigate: (reportType: ReportType) => void;
}

interface ReportCardConfig {
  id: ReportType;
  titleKey: string;
  descriptionKey: string;
  icon: typeof FileText;
  color: string;
  bgColor: string;
  adminOnly: boolean;
}

interface ReportCard {
  id: ReportType;
  title: string;
  description: string;
  icon: typeof FileText;
  color: string;
  bgColor: string;
  adminOnly: boolean;
}

const REPORT_CARDS_CONFIG: ReportCardConfig[] = [
  {
    id: 'account-statement',
    titleKey: 'reports.accountStatement.title',
    descriptionKey: 'reports.accountStatement.description',
    icon: FileText,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    adminOnly: false,
  },
  {
    id: 'delinquent',
    titleKey: 'reports.delinquent.title',
    descriptionKey: 'reports.delinquent.description',
    icon: AlertTriangle,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    adminOnly: true,
  },
  {
    id: 'annual-billing',
    titleKey: 'reports.annualBilling.title',
    descriptionKey: 'reports.annualBilling.description',
    icon: Calendar,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    adminOnly: true,
  },
  {
    id: 'income-expense',
    titleKey: 'reports.incomeExpense.title',
    descriptionKey: 'reports.incomeExpense.description',
    icon: TrendingUp,
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    adminOnly: true,
  },
  {
    id: 'monthly-statement',
    titleKey: 'reports.monthlyStatement.title',
    descriptionKey: 'reports.monthlyStatement.description',
    icon: Landmark,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    adminOnly: true,
  },
  {
    id: 'journal',
    titleKey: 'reports.journal.title',
    descriptionKey: 'reports.journal.description',
    icon: BookOpen,
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    adminOnly: true,
  },
  {
    id: 'bank-reconciliation',
    titleKey: 'reports.bankReconciliation.title',
    descriptionKey: 'reports.bankReconciliation.description',
    icon: Scale,
    color: 'text-sky-600',
    bgColor: 'bg-sky-50',
    adminOnly: true,
  },
];

export const ReportsIndexPage = ({ onNavigate }: ReportsIndexPageProps) => {
  const { t } = useI18n();
  const ability = useAbility();
  const { currentUser } = useServices();
  const isAdmin = ability.can('manage', 'all') || isSectionAdmin(currentUser);

  const reportCards: ReportCard[] = useMemo(() => 
    REPORT_CARDS_CONFIG.map(card => ({
      ...card,
      title: t(card.titleKey),
      description: t(card.descriptionKey),
    }))
  , [t]);

  const availableReports = reportCards.filter(card => !card.adminOnly || isAdmin);

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-800">{t('reports.title')}</h2>
        <p className="text-slate-500 text-sm">
          {isAdmin
            ? t('reports.admin.description')
            : t('reports.resident.description')
          }
        </p>
      </div>

      {/* Report Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        {availableReports.map((card) => (
          <button
            key={card.id}
            onClick={() => onNavigate(card.id)}
            className="flex items-start gap-4 p-6 bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300 transition-all text-left group"
          >
            <div className={`${card.bgColor} p-3 rounded-xl`}>
              <card.icon size={24} className={card.color} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-slate-800 group-hover:text-primary-600 transition-colors">
                  {card.title}
                </h3>
                {card.adminOnly && (
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded-full">
                    {t('reports.admin.title')}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-1">{card.description}</p>
            </div>
            <ChevronRight size={20} className="text-slate-300 group-hover:text-slate-500 transition-colors self-center" />
          </button>
        ))}
      </div>

      {/* Info Card for Residents */}
      {!isAdmin && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <FileText size={20} className="text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-800">{t('reports.resident.title')}</h4>
              <p className="text-sm text-blue-600 mt-1">
                {t('reports.resident.description')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
